import { useMemo } from "react";
import { useEvents } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { FileText, TrendingUp } from "lucide-react";

const typeConfig: Record<string, { emoji: string; label: string }> = {
  missile: { emoji: "🚀", label: "Missile Strikes" },
  drone: { emoji: "🛩️", label: "Drone Attacks" },
  air_raid: { emoji: "🚨", label: "Air Raids" },
  explosion: { emoji: "💥", label: "Explosions" },
  movement: { emoji: "🪖", label: "Military Movements" },
};

const DailyBrief = () => {
  const { isPremium } = useAuth();
  const { data: events } = useEvents(undefined, isPremium);

  const brief = useMemo(() => {
    if (!events?.length) return null;

    const now = Date.now();
    const last24h = events.filter(
      (e) => now - new Date(e.occurred_at).getTime() < 86400000
    );

    const byType: Record<string, number> = {};
    const byCountry: Record<string, number> = {};

    last24h.forEach((e) => {
      byType[e.event_type] = (byType[e.event_type] || 0) + 1;
      if (e.country) byCountry[e.country] = (byCountry[e.country] || 0) + 1;
    });

    const topCountries = Object.entries(byCountry)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    return { total: last24h.length, byType, topCountries };
  }, [events]);

  if (!brief || brief.total === 0) return null;

  return (
    <section className="border-b border-border">
      <div className="container mx-auto px-4 py-4 sm:py-6">
        <div className="rounded-lg border border-border bg-card p-3 sm:p-4">
          <div className="mb-2 sm:mb-3 flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
            <h3 className="font-mono text-[10px] sm:text-xs font-bold uppercase tracking-wider text-primary">
              Last 24 Hours — Intelligence Brief
            </h3>
          </div>

          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-3 sm:gap-2 md:grid-cols-5">
            {Object.entries(typeConfig).map(([type, cfg]) => {
              const count = brief.byType[type] || 0;
              return (
                <div
                  key={type}
                  className="rounded-md border border-border bg-background p-2 sm:p-3 text-center"
                >
                  <span className="text-base sm:text-lg">{cfg.emoji}</span>
                  <div className="font-mono text-lg sm:text-xl font-black text-foreground">
                    {count}
                  </div>
                  <div className="text-[9px] sm:text-[10px] text-muted-foreground leading-tight">{cfg.label}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground">
            <span className="font-mono font-bold text-foreground">
              {brief.total} total events
            </span>
            {brief.topCountries.length > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  Most affected:{" "}
                  {brief.topCountries.map(([c, n]) => `${c} (${n})`).join(", ")}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DailyBrief;
